import { Image, EndType, Period, Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { FormattedAdditionalFee } from '@wix/pricing-plans-tpa-api';
import { isFreeVariant, isRecurringVariant } from '@wix/pricing-plans-utils';
import { CoverImage } from '@wix/pricing-plans-utils/client-data';
import { CreateCurrencyFormatter, TFunction } from '@wix/yoshi-flow-editor';
import { NO_BREAK_SPACE } from '../constants';
import { getFormattedPriceData } from '../hooks';

export function planImageToCoverImage(image: Image | undefined): CoverImage | undefined {
  const { id, width, height } = image ?? {};
  if (!id || !width || !height) {
    return undefined;
  }
  return { uri: id, width, height };
}

export function getPlanPrice(plan: Plan) {
  return {
    value: plan.pricingVariants![0].pricingStrategies?.[0]?.flatRate?.amount,
    currency: plan.currency,
  };
}

export function getPlanBenefits(plan: Plan): string[] {
  return plan.perks?.map((p) => p.description ?? '') ?? [];
}

export function hasFreeTrialDays(plan: Plan): boolean {
  return !!plan.pricingVariants![0].freeTrialDays;
}

export function getFreeTrialDaysLabel(plan: Plan, t: TFunction) {
  const pricing = plan.pricingVariants![0];

  if (!pricing.freeTrialDays) {
    return '';
  }

  if (isUnlimitedPlan(plan)) {
    return t('pp.free-trial-days-unlimited', { count: pricing.freeTrialDays });
  }

  return t('pp.free-trial-days', { count: pricing.freeTrialDays });
}

export function getFormattedAdditionalFees(params: {
  plan: Plan;
  locale?: string;
  createCurrencyFormatter: CreateCurrencyFormatter;
  hideTrailingFractionZeroes?: boolean;
}): FormattedAdditionalFee[] {
  const { plan, locale, createCurrencyFormatter, hideTrailingFractionZeroes } = params;
  const pricing = plan.pricingVariants![0];

  if (!pricing.fees?.length) {
    return [];
  }

  return pricing.fees
    .filter((fee) => Boolean(fee.fixedAmountOptions?.amount))
    .map((fee) => {
      const formatted = getFormattedPriceData({
        createCurrencyFormatter,
        locale,
        value: fee.fixedAmountOptions?.amount,
        currency: plan.currency,
        hideTrailingFractionZeroes,
      });

      return { name: fee.name!, amount: `${formatted.fullPrice}` };
    });
}

export function getPlanValidityCycleV3(plan: Plan, t: TFunction) {
  if (isUnlimitedPlan(plan)) {
    if (isFreeVariant(plan.pricingVariants![0])) {
      return t('pp.plan-validity.free-plan');
    }
    return NO_BREAK_SPACE;
  } else {
    return getPlanDurationWithoutUnlimited(plan, t);
  }
}

export function getPlanDuration(plan: Plan, t: TFunction) {
  if (isUnlimitedPlan(plan)) {
    return t('pp.plan-validity.valid-unlimited');
  } else {
    return getPlanDurationWithoutUnlimited(plan, t);
  }
}

export const isUnlimitedPlan = (plan: Plan): boolean => {
  const pricing = plan.pricingVariants![0];
  return pricing?.billingTerms?.endType === EndType.UNTIL_CANCELLED;
};

export function getPeriodLabel(plan: Plan, t: TFunction) {
  const pricing = plan.pricingVariants![0];

  if (!isRecurringVariant(pricing)) {
    return null;
  }

  const unit = pricing.billingTerms?.billingCycle?.period;
  const cycleDuration = parseInt(pricing.billingTerms?.billingCycle?.count ?? '', 10);

  switch (unit) {
    case Period.DAY:
      return t('pp.period-recurrency.every-day-plural', { count: cycleDuration });
    case Period.WEEK:
      return t('pp.period-recurrency.every-week-plural', { count: cycleDuration });
    case Period.YEAR:
      return t('pp.period-recurrency.every-year-plural', { count: cycleDuration });
    case Period.MONTH:
      return t('pp.period-recurrency.every-month-plural', { count: cycleDuration });
    default:
      return '';
  }
}

function getPlanDurationWithoutUnlimited(plan: Plan, t: TFunction) {
  const pricing = plan.pricingVariants![0];
  const unit = pricing.billingTerms?.billingCycle?.period;
  const singleCycleDuration = parseInt(pricing.billingTerms?.billingCycle?.count ?? '', 10);
  const totalCycleCount = parseInt(pricing.billingTerms?.cyclesCompletedDetails?.billingCycleCount ?? '', 10);

  if (!unit || !singleCycleDuration || !totalCycleCount) {
    return '';
  }

  const planDuration = totalCycleCount * singleCycleDuration;

  switch (unit) {
    case Period.DAY:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-days', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-day');
    case Period.WEEK:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-weeks', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-week');
    case Period.YEAR:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-years', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-year');
    default:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-months', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-month');
  }
}
